import { useCallback } from 'react';
import { useBoolean } from 'usehooks-ts';
import { useTranslation } from 'react-i18next';

import { CheckCircleIcon, OnboardingContainer } from '@pletly/ui';
import { getName } from '@pletly/shared';
import { RootStackScreenProps } from '../../../../navigation/types';
import { useOrganisationMemberQuery } from '../../components/__generated__/organisationMember.generated';
import { AssignCaregiverModal } from '../../../view/components/AssignCaregiverModal';
import { withTranslation } from '../../../../locales/hoc';

export const CareRecipientCreatedConfirmation = withTranslation(
	({
		navigation: { navigate, replace },
		route: {
			params: { memberId, organisationUuid },
		},
	}: Props) => {
		const { t } = useTranslation();
		const { value: isModalOpen, setTrue: openModal, setFalse: closeModal } = useBoolean();
		const { data } = useOrganisationMemberQuery({ variables: { memberId } });

		const handleFillInformationPress = useCallback(() => {
			navigate('FillCareRecipientInformation', { memberId, organisationUuid });
		}, [navigate, memberId, organisationUuid]);

		const handleAssignCaregiversPress = useCallback(() => {
			openModal();
		}, [openModal]);

		const handleSkipPress = useCallback(() => {
			replace('Home', { uuid: organisationUuid });
		}, [replace, organisationUuid]);

		if (!data) {
			return null;
		}
		const { organisationMember } = data;

		return (
			<>
				<OnboardingContainer
					heroImage={<CheckCircleIcon />}
					title={t('care-recipient-created-confirmation.title', {
						name: getName(organisationMember.user),
					})}
					subtitle={t('care-recipient-created-confirmation.subtitle')}
					ctaText={t('care-recipient-created-confirmation.ctaButton')}
					secondaryButtonText={`${t('care-recipient-created-confirmation.secondaryButton')} (${
						organisationMember.user.superPals.length
					})`}
					onNextClick={handleFillInformationPress}
					onSecondaryClick={handleAssignCaregiversPress}
					onSkipClick={handleSkipPress}
					itemsAlignment="center"
				/>
				<AssignCaregiverModal
					visible={isModalOpen}
					onClose={closeModal}
					organisationUuid={organisationUuid}
					careRecipientMember={{
						memberId: organisationMember.id,
						...organisationMember.user,
					}}
				/>
			</>
		);
	},
);

type Props = RootStackScreenProps<'CareRecipientCreatedConfirmation'>;

export type CareRecipientCreatedConfirmationScreenParams = {
	organisationUuid: string;
	memberId: string;
};
